import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import { ErrorMessage, LoadingButton, QuickTextInput } from 'shouldve-been-mui';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import { shakeForm } from 'shouldve-been-react';
import type Vessel from '../../Models/VesselModel';
import type VesselType from '../../Models/VesselTypeModel';
import type Company from '../../Models/CompanyModel';
import { updateLastActivity } from '../../Utils/LastActivity';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { parseNumberStrict, removeDuplicates, removeNulls } from 'shouldve-been-js';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AssignSortNumberDialog from './AssignSortNumberDialog';

type AddNewVesselDialogProps = {
    controller: boolean,
    onClose: (success: boolean) => void,
    existingVessels: Vessel[],
    vesselTypes: VesselType[],
    companies: Company[],
};

function AddNewVesselDialog(props: Readonly<AddNewVesselDialogProps>): JSX.Element {

    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);
    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);

    const formRef: React.RefObject<HTMLFormElement> = useRef<HTMLFormElement>(null);

    const [pending, setPending] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [openAssignSortNumberDialog, setOpenAssignSortNumberDialog] = useState<boolean>(false);

    const [sortNo, setSortNo] = useState<number | null>(null);
    const [vesselCode, setVesselCode] = useState<string>('');
    const [vesselName, setVesselName] = useState<string>('');
    const [companyNo, setCompanyNo] = useState<string>('0');
    const [vesselTypeId, setVesselTypeId] = useState<string>('');
    const [shipNew, setShipNew] = useState<string>('');
    const [imoNumber, setImoNumber] = useState<string>('');
    const [currCode, setCurrCode] = useState<string>('');
    const [inPurchase, setInPurchase] = useState<boolean>(false);
    const [inMaintenance, setInMaintenance] = useState<boolean>(false);
    const [inBudget, setInBudget] = useState<boolean>(false);
    const [inExperience, setInExperience] = useState<boolean>(false);
    const [inVoyage, setInVoyage] = useState<boolean>(false);
    const [inLogbook, setInLogbook] = useState<boolean>(false);
    const [inDocking, setInDocking] = useState<boolean>(false);

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    const companySelectOptions: JSX.Element[] = useMemo<JSX.Element[]>(() => props.companies.map((company: Company) => {

        return (
            <MenuItem
                key={company.companyNo}
                value={company.companyNo.toString()}
                style={{
                    minHeight: '30px',
                }}
            >
                {company.name}
            </MenuItem>
        );
    }), [props.companies]);

    const vesselTypeSelectOptions: JSX.Element[] = useMemo<JSX.Element[]>(() => props.vesselTypes.map((vesselType: VesselType) => {

        return (
            <MenuItem
                key={vesselType.vesselTypeId}
                value={vesselType.vesselTypeId}
                style={{
                    minHeight: '30px',
                }}
            >
                {vesselType.vesselType}
            </MenuItem>
        );
    }), [props.vesselTypes]);

    const currencySelectOptions: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const existingCurrCodes: (string | null)[] = props.existingVessels.map((vessel: Vessel) => vessel.currCode);

        const nonNullCurrCodes: string[] = removeNulls(existingCurrCodes);

        const uniqueCurrCodes: string[] = removeDuplicates(nonNullCurrCodes);

        if (uniqueCurrCodes.length === 0) {
            return [
                <MenuItem
                    key='blank'
                    value=''
                >
                    &lt;blank&gt;
                </MenuItem>,
            ];
        } else {
            return uniqueCurrCodes.map((currentCurrCode: string) => {

                return (
                    <MenuItem
                        key={currentCurrCode}
                        value={currentCurrCode}
                        style={{
                            minHeight: '30px',
                        }}
                    >
                        {currentCurrCode}
                    </MenuItem>
                );
            });
        }

    }, [props.existingVessels]);

    const shipNewAsDate: Date | null = useMemo<Date | null>(() => {

        if (shipNew.length !== 4) {
            return null;
        }

        const monthPartString = shipNew.substring(0, 2);
        const yearPartString = shipNew.substring(2, 4);
        const monthPartNumber = Number(monthPartString);
        const yearPartNumber = Number(yearPartString);

        if (!isFinite(monthPartNumber) || !Number.isInteger(monthPartNumber) || monthPartNumber <= 0 || monthPartNumber > 12 || (monthPartNumber < 10 && monthPartString[0] !== '0')) {
            return null;
        }

        if (!isFinite(yearPartNumber) || !Number.isInteger(yearPartNumber) || yearPartNumber < 0 || yearPartNumber > 99 || (yearPartNumber < 10 && monthPartString[0] !== '0') || (yearPartNumber === 0 && yearPartString !== '00')) {
            return null;
        }

        return new Date(Date.UTC(2000 + yearPartNumber, monthPartNumber - 1));
    }, [shipNew]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    async function handleAddVessel(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        if (sortNo === null) {
            setErrorMessage('No position selected for sorting');
            return;
        } else if (vesselCode.length === 0 || vesselCode.length > 5) {
            setErrorMessage('Vessel Code must consist of between 1 and 5 characters');
            return;
        } else if (vesselName.length === 0) {
            setErrorMessage('Vessel Name cannot be empty');
            return;
        }

        if (apiContext.api === null) {
            setErrorMessage('API is not available in application');
            return;
        }

        setPending(true);
        setErrorMessage(null);

        const companyNoNum: number | null = parseNumberStrict(companyNo) ?? 0;

        const error: string | null = await apiContext.api.saveNewVessel(sortNo, vesselCode, vesselName, companyNoNum, vesselTypeId === '' ? null : vesselTypeId, shipNew, imoNumber === '' ? null : imoNumber, currCode === '' ? null : currCode, inPurchase, inMaintenance, inBudget, inExperience, inVoyage, inLogbook, inDocking, tokenContext.token);

        setPending(false);

        if (error === null) { //success
            handleClose(true);
        } else {
            shakeForm(formRef);
            setErrorMessage(error);
        }
    }

    function handleCloseAssignSortNumberDialog(newValue: number, shouldUpdate: boolean): void {

        updateLastActivity(userContext, localStorageContext);

        if (shouldUpdate) {
            setSortNo(newValue);
        }

        setOpenAssignSortNumberDialog(false);
    }

    function handleClose(success: boolean): void {

        updateLastActivity(userContext, localStorageContext);

        setSortNo(null);
        setVesselCode('');
        setVesselName('');
        setCompanyNo('0');
        setVesselTypeId('');
        setShipNew('');
        setImoNumber('');
        setCurrCode('');
        setInPurchase(false);
        setInMaintenance(false);
        setInBudget(false);
        setInExperience(false);
        setInVoyage(false);
        setInLogbook(false);
        setInDocking(false);
        setErrorMessage(null);

        props.onClose(success);
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={(): void => handleClose(false)}
        >
            <DialogTitle>Add New Vessel</DialogTitle>
            <DialogContent>
                <form
                    ref={formRef}
                    style={{
                        maxHeight: windowSize[1] * 0.65,
                        overflow: 'auto',
                    }}
                >
                    <table>
                        <tbody>
                            <tr>
                                <td style={{
                                    padding: '5px 20px 5px 0px',
                                    textAlign: 'right',
                                }}>
                                    Vessel Name:
                                </td>
                                <td style={{
                                    padding: '5px 0px',
                                }}>
                                    <QuickTextInput
                                        labelText=''
                                        value={vesselName}
                                        variant='standard'
                                        onBlur={setVesselName}
                                        dense={true}
                                        minWidth={300}
                                        fontSize='12px'
                                    /> 
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    padding: '5px 20px 5px 0px',
                                    textAlign: 'right',
                                }}>
                                    IMO Number:
                                </td>
                                <td style={{
                                    padding: '5px 0px',
                                }}>
                                    <QuickTextInput
                                        labelText=''
                                        value={imoNumber}
                                        variant='standard'
                                        onBlur={setImoNumber}
                                        dense={true}
                                        minWidth={300}
                                        fontSize='12px'
                                    /> 
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    padding: '5px 20px 5px 0px',
                                    textAlign: 'right',
                                }}>
                                    Vessel Code:
                                </td>
                                <td style={{
                                    padding: '5px 0px',
                                }}>
                                    <QuickTextInput
                                        labelText=''
                                        value={vesselCode}
                                        variant='standard'
                                        onBlur={setVesselCode}
                                        dense={true}
                                        minWidth={300}
                                        fontSize='12px'
                                    /> 
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    padding: '5px 20px 5px 0px',
                                    textAlign: 'right',
                                }}>
                                    Sorting:
                                </td>
                                <td style={{
                                    padding: '5px 0px',
                                }}>
                                    <span style={{
                                        marginRight: '20px',
                                    }}>
                                        {sortNo}
                                    </span>
                                    <Button
                                        onClick={(): void => setOpenAssignSortNumberDialog(true)}
                                        color='primary'
                                        variant='contained'
                                        style={{
                                            width: '90px',
                                            height: '36px',
                                            lineHeight: 'normal',
                                        }}
                                    >
                                        Assign
                                    </Button>
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    padding: '5px 20px 5px 0px',
                                    textAlign: 'right',
                                }}>
                                    Company:
                                </td>
                                <td style={{
                                    padding: '5px 0px',
                                }}>
                                    <FormControl style={{
                                        minWidth: 300,
                                        maxWidth: 300,
                                        textAlign: 'left',
                                    }}>
                                        <Select
                                            value={companyNo}
                                            onChange={(e: SelectChangeEvent): void => setCompanyNo((e.target as HTMLInputElement).value)}
                                            size='small'
                                        >
                                            {companySelectOptions}
                                        </Select>
                                    </FormControl>
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    padding: '5px 20px 5px 0px',
                                    textAlign: 'right',
                                }}>
                                    Vessel Type:
                                </td>
                                <td style={{
                                    padding: '5px 0px',
                                }}>
                                    <FormControl style={{
                                        minWidth: 300,
                                        maxWidth: 300,
                                        textAlign: 'left',
                                    }}>
                                        <Select
                                            value={vesselTypeId}
                                            onChange={(e: SelectChangeEvent): void => setVesselTypeId((e.target as HTMLInputElement).value)}
                                            size='small'
                                        >
                                            <MenuItem value=''>&lt;blank&gt;</MenuItem>
                                            {vesselTypeSelectOptions}
                                        </Select>
                                    </FormControl>
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    padding: '5px 20px 5px 0px',
                                    textAlign: 'right',
                                }}>
                                    Currency:
                                </td>
                                <td style={{
                                    padding: '5px 0px',
                                }}>
                                    <FormControl style={{
                                        minWidth: 300,
                                        maxWidth: 300,
                                        textAlign: 'left',
                                    }}>
                                        <Select
                                            value={currCode}
                                            onChange={(e: SelectChangeEvent): void => setCurrCode((e.target as HTMLInputElement).value)}
                                            size='small'
                                        >
                                            {currencySelectOptions}
                                        </Select>
                                    </FormControl>
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    padding: '5px 20px 5px 0px',
                                    textAlign: 'right',
                                }}>
                                    Built Date:
                                </td>
                                <td style={{
                                    padding: '5px 0px',
                                }}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}> {/*Theres a bug that makes the controlled component not be cleared when the value is cleared, but it doesn't matter that much in this case, and it is possible to clear by using backspace in input field*/}
                                        <DatePicker
                                            views={['year', 'month']}
                                            label='Year and Month'
                                            minDate={new Date('2000-01-01')}
                                            maxDate={new Date('2099-01-01')}
                                            value={shipNewAsDate}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    style: {
                                                        minWidth: 300,
                                                        maxWidth: 300,
                                                    },
                                                },
                                            }}
                                            onAccept={(value: Date | null): void => {
                                                const yearNumber: number | null = value?.getUTCFullYear() ?? null;
                                                let monthNumber: number | null = value?.getUTCMonth() ?? null;

                                                if (yearNumber === null || yearNumber < 2000 || yearNumber >= 2100) {
                                                    return;
                                                }

                                                if (monthNumber === null || monthNumber < 0 || monthNumber >= 12) {
                                                    return;
                                                }

                                                let yearPartString: string = '';

                                                if (yearNumber < 2010) {
                                                    yearPartString += '0';
                                                }

                                                yearPartString += (yearNumber - 2000).toString();

                                                monthNumber++;

                                                let monthPartString: string = '';

                                                if (monthNumber < 10) {
                                                    monthPartString += '0';
                                                }

                                                monthPartString += monthNumber.toString();

                                                setShipNew(monthPartString + yearPartString);
                                            }}
                                        />
                                    </LocalizationProvider>
                                </td>
                            </tr>
                            <tr>
                                <td
                                    colSpan={2}
                                    style={{
                                        fontWeight: 'bold',
                                        paddingTop: '20px',
                                        paddingBottom: '10px',
                                    }}
                                >
                                    Include in the following applications:
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    paddingRight: '20px',
                                    textAlign: 'right',
                                }}>
                                    Purchase:
                                </td>
                                <td>
                                    <Checkbox
                                        color='primary'
                                        checked={inPurchase}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setInPurchase(e.target.checked)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    paddingRight: '20px',
                                    textAlign: 'right',
                                }}>
                                    Maintenance:
                                </td>
                                <td>
                                    <Checkbox
                                        color='primary'
                                        checked={inMaintenance}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setInMaintenance(e.target.checked)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    paddingRight: '20px',
                                    textAlign: 'right',
                                }}>
                                    Budget:
                                </td>
                                <td>
                                    <Checkbox
                                        color='primary'
                                        checked={inBudget}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setInBudget(e.target.checked)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    paddingRight: '20px',
                                    textAlign: 'right',
                                }}>
                                    Experience:
                                </td>
                                <td>
                                    <Checkbox
                                        color='primary'
                                        checked={inExperience}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setInExperience(e.target.checked)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    paddingRight: '20px',
                                    textAlign: 'right',
                                }}>
                                    Voyage:
                                </td>
                                <td>
                                    <Checkbox
                                        color='primary'
                                        checked={inVoyage}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setInVoyage(e.target.checked)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    paddingRight: '20px',
                                    textAlign: 'right',
                                }}>
                                    Logbook:
                                </td>
                                <td>
                                    <Checkbox
                                        color='primary'
                                        checked={inLogbook}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setInLogbook(e.target.checked)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td style={{
                                    paddingRight: '20px',
                                    textAlign: 'right',
                                }}>
                                    Docking:
                                </td>
                                <td>
                                    <Checkbox
                                        color='primary'
                                        checked={inDocking}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setInDocking(e.target.checked)}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </form>

                <ErrorMessage
                    message={errorMessage}
                    maxWidth='500px'
                />

                <AssignSortNumberDialog
                    controller={openAssignSortNumberDialog}
                    currentSortNo={sortNo}
                    existingVessels={props.existingVessels}
                    onClose={handleCloseAssignSortNumberDialog}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={(): void => handleClose(false)}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '36px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Close
                </Button>
                <LoadingButton 
                    width='90px'
                    height='36px'
                    text='Add Vessel'
                    controller={pending}
                    onClick={handleAddVessel}
                    buttonColor='primary'
                    circularColor='error'
                    circularSize='30px'
                    variant='contained'
                />
            </DialogActions>
        </Dialog>
    );
}

export default AddNewVesselDialog;